import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { 
  Gift, Calendar, Users, CheckCircle, Clock, Shield, 
  AlertCircle, ShoppingBag, Loader2 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { CustomerFooter } from "@/components/CustomerFooter";
import { validateEmail } from "@/lib/emailValidation";

interface EventInfo {
  id: string;
  title: string;
  type: 'event' | 'purchase';
  date?: string;
  targetAmount?: number;
  currency: string;
  participantCount: number;
  approvalMode: 'auto' | 'manual';
  organizerName?: string;
  isFull?: boolean;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

const JoinEvent = () => {
  const { type, token } = useParams<{ type: string; token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token && type) {
      fetchEventInfo();
    }
  }, [token, type]);

  const fetchEventInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (type === 'event') {
        // Fetch gift reminder by invite token
        const { data, error } = await supabase
          .from("gift_reminders")
          .select("id, event_name, event_date, target_amount, currency, invite_link_enabled, invite_link_approval_mode, max_self_invites, customer_email")
          .eq("invite_link_token", token)
          .eq("invite_link_enabled", true)
          .single();

        if (error || !data) {
          setError("This invite link is invalid or has expired.");
          return;
        }

        // Get participant count
        const { count } = await supabase
          .from("event_invitees")
          .select("*", { count: 'exact', head: true })
          .eq("reminder_id", data.id)
          .eq("status", "active");

        // Check if max self-invites reached
        const { count: selfInviteCount } = await supabase
          .from("event_invitees")
          .select("*", { count: 'exact', head: true })
          .eq("reminder_id", data.id)
          .eq("is_self_invited", true)
          .eq("status", "active");

        const maxSelfInvites = data.max_self_invites;
        const isFull = maxSelfInvites !== null && (selfInviteCount || 0) >= maxSelfInvites;

        setEventInfo({
          id: data.id,
          title: data.event_name,
          type: 'event',
          date: data.event_date,
          targetAmount: data.target_amount,
          currency: data.currency || 'INR',
          participantCount: (count || 0) + 1, // +1 for organizer
          approvalMode: data.invite_link_approval_mode as 'auto' | 'manual',
          organizerName: data.customer_email?.split('@')[0],
          isFull,
        });
      } else if (type === 'purchase') {
        // Fetch group purchase by invite token
        const { data, error } = await supabase
          .from("group_purchases")
          .select("id, title, target_date, target_amount, currency, invite_link_enabled, invite_link_approval_mode, max_self_invites, organizer_email, organizer_name")
          .eq("invite_link_token", token)
          .eq("invite_link_enabled", true)
          .single();

        if (error || !data) {
          setError("This invite link is invalid or has expired.");
          return;
        }

        // Get participant count
        const { count } = await supabase
          .from("group_purchase_participants")
          .select("*", { count: 'exact', head: true })
          .eq("purchase_id", data.id)
          .eq("status", "active");

        // Check if max self-invites reached
        const { count: selfInviteCount } = await supabase
          .from("group_purchase_participants")
          .select("*", { count: 'exact', head: true })
          .eq("purchase_id", data.id)
          .eq("is_self_invited", true)
          .eq("status", "active");

        const maxSelfInvites = data.max_self_invites;
        const isFull = maxSelfInvites !== null && (selfInviteCount || 0) >= maxSelfInvites;

        setEventInfo({
          id: data.id,
          title: data.title,
          type: 'purchase',
          date: data.target_date,
          targetAmount: data.target_amount,
          currency: data.currency || 'INR',
          participantCount: (count || 0) + 1, // +1 for organizer
          approvalMode: data.invite_link_approval_mode as 'auto' | 'manual',
          organizerName: data.organizer_name || data.organizer_email?.split('@')[0],
          isFull,
        });
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim();

    if (!trimmedEmail) {
      toast.error("Please enter your email");
      return;
    }

    const validation = validateEmail(trimmedEmail);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid email");
      return;
    }

    if (!eventInfo) return;

    setSubmitting(true);
    try {
      const approvalStatus = eventInfo.approvalMode === 'auto' ? 'approved' : 'pending';

      if (eventInfo.type === 'event') {
        // Check if already a participant
        const { data: existing } = await supabase
          .from("event_invitees")
          .select("id, status")
          .eq("reminder_id", eventInfo.id)
          .eq("email", trimmedEmail)
          .single();

        if (existing) {
          if (existing.status === 'active') {
            toast.error("You're already a participant");
            navigate(`/events/${eventInfo.id}`);
            return;
          }
        }

        // Insert as self-invited participant
        const { error } = await supabase
          .from("event_invitees")
          .insert({
            reminder_id: eventInfo.id,
            email: trimmedEmail,
            name: trimmedName || null,
            invited_by: trimmedEmail,
            is_self_invited: true,
            approval_status: approvalStatus,
            status: approvalStatus === 'approved' ? 'active' : 'pending',
          });

        if (error) {
          if (error.message.includes('duplicate')) {
            toast.error("You've already requested to join");
            return;
          }
          throw error;
        }
      } else {
        // Group purchase
        const { data: existing } = await supabase
          .from("group_purchase_participants")
          .select("id, status")
          .eq("purchase_id", eventInfo.id)
          .eq("email", trimmedEmail)
          .single();

        if (existing) {
          if (existing.status === 'active') {
            toast.error("You're already a participant");
            navigate(`/purchases/${eventInfo.id}`);
            return;
          }
        }

        const { error } = await supabase
          .from("group_purchase_participants")
          .insert({
            purchase_id: eventInfo.id,
            email: trimmedEmail,
            name: trimmedName || null,
            invited_by: trimmedEmail,
            is_self_invited: true,
            approval_status: approvalStatus,
            status: approvalStatus === 'approved' ? 'active' : 'pending',
          });

        if (error) {
          if (error.message.includes('duplicate')) {
            toast.error("You've already requested to join");
            return;
          }
          throw error;
        }
      }

      setSubmitted(true);
      
      if (eventInfo.approvalMode === 'auto') {
        toast.success("You've joined! Redirecting...");
        // Store session
        const session = { email: trimmedEmail, method: "email" as const };
        sessionStorage.setItem("shakatah_customer_session", JSON.stringify(session));
        
        setTimeout(() => {
          navigate(eventInfo.type === 'event' 
            ? `/events/${eventInfo.id}` 
            : `/purchases/${eventInfo.id}`
          );
        }, 1500);
      } else {
        toast.success("Request sent! The organizer will review your request.");
      }
    } catch (err) {
      console.error("Error joining:", err);
      toast.error("Failed to join. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !eventInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Invalid Invite Link</h1>
            <p className="text-muted-foreground mb-4">
              {error || "This invite link is not valid."}
            </p>
            <Button onClick={() => navigate("/")} variant="outline">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currencySymbol = CURRENCY_SYMBOLS[eventInfo.currency] || eventInfo.currency;
  const isEvent = eventInfo.type === 'event';
  const gradientClass = isEvent 
    ? "from-pink-500 via-rose-500 to-pink-600" 
    : "from-purple-500 via-violet-500 to-purple-600";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title={`Join ${eventInfo.title}`}
        description={`You're invited to join "${eventInfo.title}"`}
      />
      
      <div className="flex-1 container py-8 max-w-md mx-auto">
        <Card className="overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${gradientClass} p-6 text-white`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                {isEvent ? <Gift className="w-6 h-6" /> : <ShoppingBag className="w-6 h-6" />}
              </div>
              <div>
                <Badge className="bg-white/20 text-white text-xs mb-1">
                  {isEvent ? "Gift Event" : "Group Purchase"}
                </Badge>
                <h1 className="text-xl font-bold">{eventInfo.title}</h1>
              </div>
            </div>

            {eventInfo.date && (
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(eventInfo.date), "MMMM d, yyyy")}</span>
              </div>
            )}
          </div>

          <CardContent className="pt-6 space-y-6">
            {/* Stats */}
            <div className="flex items-center justify-around text-center">
              <div>
                <div className="flex items-center justify-center gap-1 text-2xl font-bold">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  {eventInfo.participantCount}
                </div>
                <p className="text-xs text-muted-foreground">Participants</p>
              </div>
              
              {eventInfo.targetAmount && eventInfo.targetAmount > 0 && (
                <div>
                  <div className="text-2xl font-bold">
                    {currencySymbol}{eventInfo.targetAmount.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">Goal</p>
                </div>
              )}
            </div>

            {/* Approval mode indicator */}
            {eventInfo.approvalMode === 'manual' && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg text-sm">
                <Shield className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700">
                  Verified by organizer - your request will be reviewed
                </span>
              </div>
            )}

            {/* Organizer */}
            {eventInfo.organizerName && (
              <p className="text-sm text-muted-foreground text-center">
                Organized by <span className="font-medium">{eventInfo.organizerName}</span>
              </p>
            )}

            {/* Join Form or Full Message */}
            {submitted ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">
                  {eventInfo.approvalMode === 'auto' 
                    ? "You're in!" 
                    : "Request Sent"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {eventInfo.approvalMode === 'auto'
                    ? "Redirecting to the event..."
                    : "The organizer will review your request and you'll receive an email once approved."}
                </p>
              </div>
            ) : eventInfo.isFull ? (
              <div className="text-center py-4">
                <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold mb-1">Registration Closed</h3>
                <p className="text-sm text-muted-foreground">
                  This {isEvent ? 'event' : 'group purchase'} has reached its maximum participants.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Your email *"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <Input
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <Button 
                  onClick={handleJoin} 
                  disabled={submitting || !email.trim()}
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : eventInfo.approvalMode === 'auto' ? (
                    "Join Now"
                  ) : (
                    "Request to Join"
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  By joining, you agree to our terms of service
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <CustomerFooter />
    </div>
  );
};

export default JoinEvent;
